/** @file Skinned vertex-count change: rigid → edit → skinned → weights, bind re-captured at setup (W2-29). */
import { setupWorldOf } from '@pose2d/commands/mesh/setupWorldOf';
import { skinBlend } from '@pose2d/commands/mesh/skinBlend';
import { transformMeshPoints } from '@pose2d/commands/mesh/transformMeshPoints';
import type { CommandCall, Doc } from '@pose2d/format/types';
import { affine } from '@pose2d/math/affine';
import type { Affine, Vec2 } from '@pose2d/math/types';
import { invariant } from '@pose2d/std/invariant';
import type { MeshTarget } from '../meshTarget';
import { blendSources, type TopologyPlan } from './blendSources';
import { deformRewrites } from './deformRewrites';
import { shapeCalls } from './shapeCalls';

/** Every point of `flat` through one affine. */
function mapPoints(flat: readonly number[], m: Affine): number[] {
  const out: number[] = [];
  const p: Vec2 = [0, 0];
  for (let i = 0; i + 1 < flat.length; i += 2) out.push(...affine.apply(m, flat[i] ?? 0, flat[i + 1] ?? 0, p));
  return out;
}

/**
 * mesh.setVertices refuses a count change on a skinned mesh, so the batch converts it to rigid on the target slot's
 * bone, stores the new topology, converts back and sets the rebuilt weights. The result is the rebindMesh form: bind
 * vertices = setup positions, bind poses = setup worlds, deform δ' = Lin(Mᵥ)·δ with Mᵥ the setup skinning affine.
 * A shrinking plan stores the shape before the vertices, so mesh.setVertices drops no triangles.
 */
export function skinnedRoundTrip(doc: Doc, target: MeshTarget, plan: TopologyPlan, uvs: readonly number[]): CommandCall[] {
  const { attachment, mesh } = target;
  const g = mesh.geometry;
  const bone = doc.slots[target.slot]?.bone;
  invariant(g.kind === 'skinned' && bone !== undefined, 'skinnedRoundTrip needs a skinned mesh on an existing slot');
  const worldOf = setupWorldOf(doc);
  const mats = skinBlend(g, worldOf, `attachments.${attachment}.geometry`);
  const shape = shapeCalls(attachment, mesh, plan, true);
  const shrink = plan.sources.length < mesh.uvs.length / 2;
  const rigid = mapPoints(transformMeshPoints(g.bindVertices, mats, 0, false), affine.invert(new Float64Array(6), worldOf(bone)));
  return [
    { name: 'mesh.setBinding', input: { target: attachment, binding: 'rigid', bone } },
    ...(shrink ? shape : []),
    { name: 'mesh.setVertices', input: { target: attachment, vertices: blendSources.pairs(rigid, plan.sources), uvs: [...uvs] } },
    ...(shrink ? [] : shape),
    { name: 'mesh.setBinding', input: { target: attachment, binding: 'skinned', bone } },
    { name: 'mesh.setWeights', input: { target: attachment, weights: blendSources.rows(g.weights, plan.sources) } },
    ...deformRewrites(doc, attachment, plan, mesh.uvs.length / 2, (full) => transformMeshPoints(full, mats, 0, true)),
  ];
}
